// Fiche dossier dédiée OMV France (CEE) — vue early-return, gatée tenant=omv_france
// par l'appelant (DossierDetail.jsx). AUCUN code TEF/AMO ici : pas de pipeline 9 étapes,
// pas de MaPrimeAdapt, pas d'éligibilité résidentielle, pas de Conformité/Compte ANAH,
// pas d'Opération AMO. Header condensé + 5 sections (réf SPEC-CEE-OMV-DOSSIER + CDC §2.3).
// Statut = champ libre éditable, options = liste `statuts_dossier` (metier_config_listes,
// éditable en Gouvernance) avec repli sur STATUTS_CONFIG.climat tant que la liste est vide.
// Édition CEE via PATCH /api/metier/dossiers/{id} (OmvCeeEditor). Statut via /statut
// (set_dossier_statut, writer canonique — pas de moteur de statuts ici, §B#3=V2).
import { useState } from "react";
import { ArrowLeft, Building2, MapPin, Hash, Trash2, MoreVertical, Receipt, FolderOpen, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { useConfigList } from "../../hooks/useConfigList";
import { STATUTS_CONFIG } from "../../pages/metier/dossier/helpers";
import OmvCeeEditor from "./OmvCeeEditor";
import AttestationHonneurBouton from "./AttestationHonneurBouton";
import DevisFacturesTab from "../../pages/metier/DevisFacturesTab";
import { DocumentsForm } from "../../pages/metier/dossier/DocumentsForm";
import { HistoriqueModal } from "../../pages/metier/dossier/HistoriqueModal";
import { CommentairesDossier } from "../../pages/metier/dossier/CommentairesDossier";

const CARD = "rounded-lg p-4";
const CARD_STYLE = { background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" };

// Sélecteur de statut OMV : liste éditable (metier_config_listes type "statuts_dossier"),
// repli sur les statuts climat connus, et on garantit la présence du statut courant.
function StatutOmvSelect({ dossier, onStatutChange, readOnly }) {
  const { items } = useConfigList("statuts_dossier");
  const climatCfg = STATUTS_CONFIG.climat || {};
  let options = (items && items.length)
    ? items.map((i) => ({ value: i.id, label: i.label }))
    : Object.entries(climatCfg).map(([id, c]) => ({ value: id, label: c.label }));
  if (dossier.statut && !options.some((o) => o.value === dossier.statut)) {
    options = [{ value: dossier.statut, label: climatCfg[dossier.statut]?.label || dossier.statut }, ...options];
  }
  return (
    <select
      value={dossier.statut || ""}
      disabled={readOnly}
      onChange={(e) => onStatutChange?.(e.target.value)}
      data-testid="omv-statut-select"
      aria-label="Statut du dossier"
      className="rounded px-3 py-1.5 text-sm font-medium"
      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)", colorScheme: "light dark" }}
    >
      {!dossier.statut && <option value="">Statut…</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export default function DossierOmvView({
  dossier, dossierId, tenantId, documents = [], historique = [], onBack, onUpdate, onNavigate,
  onStatutChange, onDelete, isTEFRole = false,
}) {
  const [showHistorique, setShowHistorique] = useState(false);
  const ent = dossier.entreprise || {};
  const bat = dossier.batiment || {};
  const raisonSociale = ent.raison_sociale || dossier.client_nom || "Entreprise bénéficiaire";
  // D6 #3 — localisation = CP + VILLE des TRAVAUX uniquement (champs structurés,
  // jamais le siège bénéficiaire). Pas d'adresse texte libre.
  const cpVilleTravaux = [bat.cp_travaux, bat.ville_travaux].filter(Boolean).join(" ");
  // Numéro AUTO uniquement (référence générée) — plus de fallback sur le
  // numero_dossier saisi manuellement (input supprimé, doublon avec le header).
  const numero = dossier.reference || dossier.référence || dossierId;

  return (
    <div className="space-y-4" data-testid="dossier-omv-view">
      {/* ═══ HEADER CONDENSÉ (pas de pipeline 9 étapes AMO) ═══ */}
      <div className={CARD} style={CARD_STYLE} data-testid="omv-header">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="Retour">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-5 h-5 flex-shrink-0" style={{ color: "var(--oh-stat-orange-text, #fb923c)" }} />
            <div className="min-w-0">
              <div className="text-base font-medium truncate" style={{ color: "var(--oh-text)" }} data-testid="omv-raison-sociale">
                {raisonSociale}
              </div>
              <div className="flex items-center gap-3 text-xs flex-wrap" style={{ color: "var(--oh-text-muted)" }}>
                <span className="flex items-center gap-1" data-testid="omv-numero"><Hash className="w-3 h-3" />{numero}</span>
                {cpVilleTravaux && <span className="flex items-center gap-1" title="CP / Ville des travaux"><MapPin className="w-3 h-3" />{cpVilleTravaux}</span>}
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <StatutOmvSelect dossier={dossier} onStatutChange={onStatutChange} readOnly={!isTEFRole} />
            <Button
              data-testid="historique-btn"
              size="sm"
              variant="ghost"
              className="p-1.5"
              aria-label="Historique des modifications"
              onClick={() => setShowHistorique(true)}
            >
              <Clock className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            </Button>
            {isTEFRole && onDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="Actions du dossier"><MoreVertical className="w-4 h-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onDelete} className="text-[var(--oh-error-text)] dark:text-[var(--oh-error-text)]" data-testid="omv-delete">
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer le dossier
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* ═══ SECTIONS 1-3 : Entreprise & Représentant · Bâtiment & Technique · Opération CEE ═══ */}
      <OmvCeeEditor dossier={dossier} dossierId={dossierId} tenantId={tenantId} onUpdate={onUpdate} readOnly={!isTEFRole} />

      {/* T-177 — attestation sur l'honneur pré-remplie, rangée dans les pièces du dossier (module CEE d'OMV) */}
      {isTEFRole && (
        <div className={CARD} style={CARD_STYLE} data-testid="omv-attestation-honneur">
          <h3 className="text-sm font-medium mb-3" style={{ color: "var(--oh-text)" }}>Attestation sur l'honneur</h3>
          <AttestationHonneurBouton dossierId={dossierId} tenantId={tenantId} onUpdate={onUpdate} />
        </div>
      )}

      {/* ═══ SECTION 4 : Devis de ce dossier (génération native OMV, prime → net 0, branding OMV) ═══ */}
      <div className={CARD} style={CARD_STYLE} data-testid="omv-devis">
        <h3 className="text-sm font-medium flex items-center gap-2 mb-3" style={{ color: "var(--oh-text)" }}>
          <Receipt className="w-4 h-4" style={{ color: "var(--oh-stat-orange-text, #fb923c)" }} /> Devis de ce dossier
        </h3>
        <DevisFacturesTab dossierId={dossierId} tenantId={tenantId} dossier={dossier} onNavigate={onNavigate} />
      </div>

      {/* ═══ SECTION 5 : Documents (pièces du dossier — mécanisme GED partagé) ═══ */}
      <div className={CARD} style={CARD_STYLE} data-testid="omv-documents">
        <h3 className="text-sm font-medium flex items-center gap-2 mb-3" style={{ color: "var(--oh-text)" }}>
          <FolderOpen className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          Documents {documents.length > 0 && `(${documents.length})`}
        </h3>
        {/* tenant = celui DU DOSSIER (dossier.tenant_id), pas le tenant courant
            de l'utilisateur : un super_admin (JJ) qui ouvre un dossier OMV doit
            voir la liste de types ET la GED du tenant omv_france. L'endpoint
            /config/document-types honore X-Tenant-ID pour les super_admin →
            sans ça il renvoyait la liste du tenant courant de JJ (TEF). */}
        <DocumentsForm dossierId={dossierId} tenantId={dossier?.tenant_id || tenantId} documents={documents}
          verrouille={dossier.verrouille} onUpdate={onUpdate}
          clientNom={dossier.client_nom} dossierRef={dossier.reference || dossier.référence} />
      </div>

      {/* ═══ SECTION 6 : Fil de commentaires (brique metier_hub partagée, multi-tenant) ═══ */}
      {/* Même composant que la vue TEF (CommercialSection) : back déjà prêt
          (POST persiste tenant_id), 0 couplage TEF. Cf. AUDIT-FIL-COMMENTAIRES
          -REUTILISABILITE-TEF-OMV-2026-06-14. */}
      <CommentairesDossier dossierId={dossierId} tenantId={tenantId} verrouille={dossier.verrouille} />

      {showHistorique && (
        <HistoriqueModal historique={historique} onClose={() => setShowHistorique(false)} />
      )}
    </div>
  );
}
